import { useState } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useSnackbar } from 'notistack'
import { useAiProductsApi, AiExtractedProduct } from './useAiProductsApi'
import { AiProductPreview } from './AiProductPreview'
import { useProductApi } from '../../organizer/events/api/useProductApi'
import { useCompanyContext } from '../../../contexts/company-context'
import { useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

/**
 * SuperAdmin-only page: Create product from free-text using Gemini AI.
 *
 * Flow:
 * 1. SuperAdmin pastes a text description of an offer
 * 2. Click "Analizuj" → backend calls Gemini, returns structured data
 * 3. Preview/edit the extracted data
 * 4. Click "Utwórz produkt" → creates product via existing API
 */
export const AiCreateProductPage = () => {
    const [inputText, setInputText] = useState('')
    const [isExtracting, setIsExtracting] = useState(false)
    const [extractedData, setExtractedData] = useState<AiExtractedProduct | null>(
        null
    )
    const [editedData, setEditedData] = useState<AiExtractedProduct | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isCreating, setIsCreating] = useState(false)

    const { extractProductFromText } = useAiProductsApi()
    const { createProduct, createTrip } = useProductApi()
    const companyContext = useCompanyContext()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()

    const handleExtract = async () => {
        if (inputText.trim().length < 20) {
            setError('Tekst musi mieć co najmniej 20 znaków.')
            return
        }

        setIsExtracting(true)
        setError(null)
        setExtractedData(null)
        setEditedData(null)

        try {
            const data = await extractProductFromText(inputText)
            setExtractedData(data)
            setEditedData(data)
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Wystąpił błąd podczas analizy tekstu.'
            setError(message)
        } finally {
            setIsExtracting(false)
        }
    }

    const handleCreate = async () => {
        if (!editedData) return

        if (!companyContext.company?.id) {
            enqueueSnackbar('Wybierz firmę przed utworzeniem produktu.', {
                variant: 'warning'
            })
            return
        }

        setIsCreating(true)
        try {
            const productId = uuidv4()

            // Build category object from first category if available
            const category = editedData.categories?.[0]
                ? { id: editedData.categories[0], code: editedData.categories[0] }
                : { id: 'inne', code: 'inne' }

            const productPayload: any = {
                id: productId,
                name: editedData.name,
                type: editedData.type,
                description: editedData.description,
                company_id: companyContext.company.id,
                category: category,
                categories: editedData.categories,
                min_age: editedData.min_age,
                max_age: editedData.max_age,
                duration_days: editedData.duration_days,
                trip_type: editedData.trip_type,
                country_code: editedData.country_code ?? 'PL',
                country_name: editedData.country_name ?? 'Polska',
                location: editedData.location,
                location_types: editedData.location_types,
                location_name: editedData.location_name,
                transport_type: editedData.transport_type,
                transport_description: editedData.transport_description,
                food_type: editedData.food_type,
                food_description: editedData.food_description,
                food_vegetarian: editedData.food_vegetarian,
                food_vegan: editedData.food_vegan,
                insurance_type: editedData.insurance_type,
                insurance_description: editedData.insurance_description,
                schedule_description: editedData.schedule_description,
                schedule: editedData.schedule,
                participation_conditions: editedData.participation_conditions,
                conditions_text_short: editedData.conditions_text_short,
                conditions_text_long: editedData.conditions_text_long,
                additional_info_text: editedData.additional_info_text,
                min_price: editedData.min_price,
                price_description: editedData.price_description,
                price_includes_text: editedData.price_includes_text,
                price_not_includes_text: editedData.price_not_includes_text,
                discounts_description: editedData.discounts_description,
                tags: editedData.tags,
                availability: editedData.availability
            }

            // Remove undefined values
            Object.keys(productPayload).forEach(key => {
                if (productPayload[key] === undefined) {
                    delete productPayload[key]
                }
            })

            const result = await createProduct(productPayload)
            const createdProductId = result?.product?.id ?? productId

            // Create trips if extracted
            if (editedData.trips && editedData.trips.length > 0) {
                for (const trip of editedData.trips) {
                    try {
                        const tripId = uuidv4()
                        await createTrip(createdProductId, {
                            id: tripId,
                            product_id: createdProductId,
                            name: trip.name ?? editedData.name,
                            start_date: trip.start_date,
                            end_date: trip.end_date,
                            full_price: trip.full_price,
                            advance_price: trip.advance_price,
                            available_spots: trip.available_spots,
                            company_id: companyContext.company.id,
                            product_type: editedData.type,
                            currency: 'PLN'
                        })
                    } catch (tripErr) {
                        console.error('Błąd tworzenia terminu:', tripErr)
                    }
                }
            }

            enqueueSnackbar(
                `Produkt "${editedData.name}" został utworzony${editedData.trips?.length
                    ? ` wraz z ${editedData.trips.length} terminami`
                    : ''
                }.`,
                { variant: 'success' }
            )

            // Navigate to the product edit page
            const typeRoute = mapProductTypeToRoute(editedData.type)
            navigate(`/products/${typeRoute}/${createdProductId}/details`)
        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Wystąpił błąd podczas tworzenia produktu.'
            enqueueSnackbar(message, { variant: 'error' })
        } finally {
            setIsCreating(false)
        }
    }

    const handleReset = () => {
        setExtractedData(null)
        setEditedData(null)
        setError(null)
    }

    return (
        <div className="max-w-[1200px]">
            <div className="flex items-center gap-3 mb-6">
                <AutoAwesomeIcon sx={{ fontSize: 32, color: '#6366f1' }} />
                <h1 className="text-[28px] font-bold">
                    Utwórz produkt z tekstu (AI)
                </h1>
            </div>

            <p className="text-gray-600 mb-6">
                Wklej pełny opis oferty turystycznej (np. ze strony WWW, maila, PDF).
                Gemini AI automatycznie wyekstraktuje wszystkie dane potrzebne do
                utworzenia produktu: typ oferty, lokalizację, terminy, ceny,
                wyżywienie, transport i inne szczegóły.
            </p>

            {/* Step 1: Text input */}
            {!extractedData && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Krok 1: Wklej opis oferty
                    </h2>
                    <TextField
                        multiline
                        minRows={10}
                        maxRows={25}
                        fullWidth
                        value={inputText}
                        onChange={e => setInputText(e.target.value)}
                        placeholder="Wklej tutaj pełny tekst opisu oferty turystycznej..."
                        variant="outlined"
                        disabled={isExtracting}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: 'monospace',
                                fontSize: '14px'
                            }
                        }}
                    />
                    <div className="flex items-center gap-4 mt-4">
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleExtract}
                            disabled={isExtracting || inputText.trim().length < 20}
                            startIcon={
                                isExtracting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <AutoAwesomeIcon />
                                )
                            }
                            sx={{
                                backgroundColor: '#6366f1',
                                '&:hover': { backgroundColor: '#4f46e5' }
                            }}
                        >
                            {isExtracting ? 'Analizuję...' : 'Analizuj tekst z AI'}
                        </Button>
                        {inputText.length > 0 && (
                            <span className="text-sm text-gray-400">
                                {inputText.length} znaków
                            </span>
                        )}
                    </div>

                    {error && (
                        <Alert severity="error" className="mt-4">
                            {error}
                        </Alert>
                    )}
                </div>
            )}

            {/* Step 2: Preview & edit extracted data */}
            {extractedData && editedData && (
                <div>
                    <Alert severity="success" className="mb-4">
                        AI wyekstraktowało dane z tekstu. Sprawdź i popraw jeśli
                        potrzebne, a następnie utwórz produkt.
                    </Alert>

                    <AiProductPreview
                        data={editedData}
                        onChange={setEditedData}
                    />

                    <div className="flex gap-4 mt-6 mb-8 sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border">
                        <Button
                            variant="contained"
                            size="large"
                            color="success"
                            onClick={handleCreate}
                            disabled={isCreating || !companyContext.company?.id}
                            startIcon={
                                isCreating ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : null
                            }
                        >
                            {isCreating ? 'Tworzę produkt...' : 'Utwórz produkt'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={handleReset}
                            disabled={isCreating}
                        >
                            Analizuj inny tekst
                        </Button>
                        {!companyContext.company?.id && (
                            <Alert severity="warning" sx={{ py: 0 }}>
                                Wybierz firmę w nagłówku, aby utworzyć produkt.
                            </Alert>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

function mapProductTypeToRoute(type: string): string {
    switch (type) {
        case 'kolonie':
            return 'camp'
        case 'polkolonie':
            return 'daycamp'
        case 'wycieczka-szkolna':
        case 'zielona-szkola':
            return 'schooltrip'
        case 'noclegi-dla-grup':
            return 'groups'
        default:
            return 'camp'
    }
}
